
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CSVLink } from "react-csv";
import { Container, Segment, Header } from 'semantic-ui-react'
import { useQuery, useLazyQuery, useMutation, gql } from '@apollo/client';

import { ResponsiveContainer } from '../ResponsiveContainer';
import { PATHS } from '../../paths';

// ── GraphQL ────────────────────────────────────────────────────────────────────

const GET_PEOPLE_LIST = gql`query Peoplelist { peoplelist { dataJSON } }`;
const GET_AUDIT_DATA  = gql`query Audit($tagId: Int) { audit(tagId: $tagId) { dataJSON } }`;
const SKU_EXISTS      = gql`query SkuExists($sku: String!) { skuExists(sku: $sku) }`;
const RENAME_SKU      = gql`
  mutation RenameSku($oldSku: String!, $newSku: String!) {
    renameSku(oldSku: $oldSku, newSku: $newSku)
  }
`;

const buildReportQuery = (name: string) => gql`
  query ${name} { ${name} { dataJSON } }
`;

const GET_BLOCK_TAGS = gql`query BlockTags { blockTags { id name code } }`;
const GET_BLOCK_ATTENDANCE = gql`
  query AttendanceTotalForBlock($blockTagId: Int!) {
    attendanceTotalForBlock(blockTagId: $blockTagId) { dataJSON }
  }
`;

// ── Report button style ────────────────────────────────────────────────────────

const ReportBtn = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? '#e8f0fe' : '#f1f5f9',
        border: '1px solid ' + (hover ? '#4a80f0' : '#cbd5e1'),
        borderRadius: '5px',
        padding: '0.3em 0.75em',
        fontSize: '0.83em',
        fontWeight: 500,
        color: hover ? '#1a56db' : '#374151',
        cursor: 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
};

// ── Report helpers ─────────────────────────────────────────────────────────────

const PeopleList = () => {
  const { loading, error, data } = useQuery(GET_PEOPLE_LIST);
  if (loading || error || !data?.peoplelist?.[0]) return <span style={s.generating}>Generating…</span>;
  return <CSVLink data={JSON.parse(data.peoplelist[0].dataJSON)} style={s.csvLink}>↓ People list</CSVLink>;
};

const TruencyReport = ({ tagId }: { tagId: any }) => {
  const { loading, error, data } = useQuery(GET_AUDIT_DATA, {
    variables: { tagId: tagId === -1 ? null : tagId },
  });
  if (loading || error || !data?.audit?.[0]) return <span style={s.generating}>Generating…</span>;
  return <CSVLink data={JSON.parse(data.audit[0].dataJSON)} style={s.csvLink}>↓ Download</CSVLink>;
};

const ReportLoad = ({ reportName, label }: { reportName: string; label: string }) => {
  const { loading, error, data } = useQuery(buildReportQuery(reportName));
  if (loading || error || !data?.[reportName]?.[0]) return <span style={s.generating}>Generating…</span>;
  return <CSVLink data={JSON.parse(data[reportName][0].dataJSON)} style={s.csvLink}>↓ {label}</CSVLink>;
};

const Report = ({ reportName, label }: { reportName: string; label: string }) => {
  const [loaded, setLoaded] = useState(false);
  if (loaded) return <ReportLoad reportName={reportName} label={label} />;
  return <ReportBtn onClick={() => setLoaded(true)}>{label}</ReportBtn>;
};

const ReportGrid = ({ children }: { children: React.ReactNode }) => (
  <div style={s.reportGrid}>{children}</div>
);

const BlockAttendanceLoad = ({ tagId, label }: { tagId: number; label: string }) => {
  const { loading, error, data } = useQuery(GET_BLOCK_ATTENDANCE, { variables: { blockTagId: tagId } });
  if (loading || error || !data?.attendanceTotalForBlock?.[0]) return <span style={s.generating}>Generating…</span>;
  return <CSVLink data={JSON.parse(data.attendanceTotalForBlock[0].dataJSON)} style={s.csvLink}>↓ {label}</CSVLink>;
};

const BlockAttendanceReport = ({ tagId, label }: { tagId: number; label: string }) => {
  const [loaded, setLoaded] = useState(false);
  if (loaded) return <BlockAttendanceLoad tagId={tagId} label={label} />;
  return <ReportBtn onClick={() => setLoaded(true)}>{label}</ReportBtn>;
};

const AttendanceByBlock = () => {
  const { loading, error, data } = useQuery(GET_BLOCK_TAGS);
  if (loading) return <span style={s.generating}>Loading blocks…</span>;
  if (error || !data?.blockTags?.length) return <span style={s.generating}>No blocks found</span>;
  const sortedTags = [...data.blockTags].sort((a: any, b: any) => a.id - b.id);
  return (
    <ReportGrid>
      {sortedTags.map((tag: any) => (
        <BlockAttendanceReport key={tag.id} tagId={tag.id} label={tag.name || tag.code} />
      ))}
    </ReportGrid>
  );
};

// ── SKU validation ─────────────────────────────────────────────────────────────

const SKU_SEGMENTS = [
  { name: 'Location',  pattern: /^[A-Z]{2,3}$/,                hint: 'e.g. KP, SG' },
  { name: 'Stream',    pattern: /^[A-Z]{2,3}$/,                hint: 'e.g. VT, ED, SR' },
  { name: 'Subject',   pattern: /^[A-Z]{2,3}$/,                hint: 'e.g. BS, AR, SW' },
  { name: 'Block',     pattern: /^[A-Z]{2,4}$/,                hint: 'e.g. NE, DS, PP' },
  { name: 'Level',     pattern: /^L[1-9][a-z]?$/,              hint: 'e.g. L1, L2a, L2b' },
  { name: 'Class',     pattern: /^C[0-9]{1,2}$/,               hint: 'e.g. C1, C10' },
  { name: 'Term',      pattern: /^(JAN|APR|JUL|OCT)[0-9]{4}$/, hint: 'e.g. JAN2026, APR2026' },
];

function validateSkuFormat(sku: string): string[] {
  const parts = sku.split('-');
  if (parts.length !== 7) {
    return [`Must have exactly 7 dash-separated segments (has ${parts.length})`];
  }
  return SKU_SEGMENTS
    .map(({ name, pattern, hint }, i) =>
      pattern.test(parts[i]) ? null : `Segment ${i + 1} (${name}): "${parts[i]}" is invalid — ${hint}`)
    .filter(Boolean) as string[];
}

// ── Success overlay ────────────────────────────────────────────────────────────

const REDIRECT_SECONDS = 5;

const SuccessOverlay = ({
  oldSku, newSku, courseId, onDismiss,
}: {
  oldSku: string; newSku: string; courseId: number; onDismiss: () => void;
}) => {
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Fade in
    const fadeTimer = setTimeout(() => setVisible(true), 30);

    // Countdown
    const interval = setInterval(() => {
      setSeconds(s => {
        if (s <= 1) {
          clearInterval(interval);
          navigate(PATHS.CLASS.replace(':id', String(courseId)));
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => { clearTimeout(fadeTimer); clearInterval(interval); };
  }, [courseId, navigate]);

  return (
    <div style={{ ...s.successOverlay, opacity: visible ? 1 : 0 }}>
      <div style={s.successIcon}>✅</div>
      <div style={s.successTitle}>SKU renamed successfully</div>

      <div style={s.skuChangeDisplay}>
        <span style={s.oldSkuDisplay}>{oldSku}</span>
        <span style={s.arrowDisplay}>→</span>
        <span style={s.newSkuDisplay}>{newSku}</span>
      </div>

      <div style={s.tagWarning}>
        <span style={s.tagWarningIcon}>⚠️</span>
        <div>
          <strong>Tags have not been automatically updated.</strong><br />
          You will need to update the tag for this course manually on the course page.
        </div>
      </div>

      <div style={s.redirectMsg}>
        We are sending you there now — redirecting in {seconds}s
      </div>

      <button
        onClick={() => navigate(PATHS.CLASS.replace(':id', String(courseId)))}
        style={s.goNowBtn}
      >
        Go now
      </button>
      <button onClick={onDismiss} style={s.dismissBtn}>Stay on this page</button>
    </div>
  );
};

// ── SKU Rename tool ────────────────────────────────────────────────────────────

const SkuRenameSection = () => {
  const [oldSku, setOldSku] = useState('');
  const [newSku, setNewSku] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [renamedInfo, setRenamedInfo] = useState<{ oldSku: string; newSku: string; courseId: number } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [checkOld, oldResult] = useLazyQuery(SKU_EXISTS, { fetchPolicy: 'network-only' });
  const [checkNew, newResult] = useLazyQuery(SKU_EXISTS, { fetchPolicy: 'network-only' });
  const [renameSku, { loading: renaming }] = useMutation(RENAME_SKU);

  const oldFormatErrors = oldSku ? validateSkuFormat(oldSku) : [];
  const newFormatErrors = newSku ? validateSkuFormat(newSku) : [];
  const oldFormatOk = oldSku.length > 0 && oldFormatErrors.length === 0;
  const newFormatOk = newSku.length > 0 && newFormatErrors.length === 0;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (oldFormatOk) checkOld({ variables: { sku: oldSku } });
      if (newFormatOk) checkNew({ variables: { sku: newSku } });
    }, 500);
  }, [oldSku, newSku, oldFormatOk, newFormatOk]);

  const oldExists  = oldResult.data?.skuExists === true;
  const newExists  = newResult.data?.skuExists === true;
  const oldChecked = oldResult.called && !oldResult.loading && oldFormatOk;
  const newChecked = newResult.called && !newResult.loading && newFormatOk;

  const issues: string[] = [];
  if (!oldSku) issues.push('Enter the current (old) SKU');
  else oldFormatErrors.forEach(e => issues.push(`Old SKU — ${e}`));
  if (oldFormatOk && oldChecked && !oldExists) issues.push('Old SKU does not exist in the database');

  if (!newSku) issues.push('Enter the replacement (new) SKU');
  else newFormatErrors.forEach(e => issues.push(`New SKU — ${e}`));
  if (newFormatOk && newChecked && newExists) issues.push('New SKU already exists — choose a different one');

  if (oldSku && newSku && oldSku === newSku) issues.push('Old and new SKUs cannot be the same');

  const ready = issues.length === 0 && oldChecked && newChecked && oldExists && !newExists;

  const handleRename = async () => {
    if (!ready || !confirmed) return;
    try {
      const result = await renameSku({ variables: { oldSku, newSku } });
      const courseId = result.data.renameSku;
      setRenamedInfo({ oldSku, newSku, courseId });
    } catch (e: any) {
      alert(`Rename failed: ${e.message}`);
    }
  };

  if (renamedInfo) {
    return (
      <div style={s.dangerSection}>
        <SuccessOverlay
          oldSku={renamedInfo.oldSku}
          newSku={renamedInfo.newSku}
          courseId={renamedInfo.courseId}
          onDismiss={() => {
            setRenamedInfo(null);
            setOldSku(''); setNewSku(''); setConfirmed(false);
          }}
        />
      </div>
    );
  }

  return (
    <div style={s.dangerSection}>
      <div style={s.dangerHeader}>⚠️ SKU Correction Tool — Admins Only</div>
      <p style={s.dangerWarning}>
        <strong>This tool permanently renames a course SKU in the database.</strong> Only use this
        to fix a SKU that was entered incorrectly. Do not use this to move a course to a different
        block or term — that is a different operation. If you are unsure, stop and ask first.
      </p>

      <div style={s.skuInputRow}>
        <div style={s.skuInputGroup}>
          <label style={s.skuLabel}>Current SKU (incorrect value)</label>
          <input
            style={s.skuInput}
            value={oldSku}
            onChange={e => { setOldSku(e.target.value.trim().toUpperCase()); setConfirmed(false); }}
            placeholder="KP-VT-BS-NE-L1-C1-JAN2026"
          />
        </div>
        <div style={s.arrow}>→</div>
        <div style={s.skuInputGroup}>
          <label style={s.skuLabel}>Replacement SKU (correct value)</label>
          <input
            style={s.skuInput}
            value={newSku}
            onChange={e => { setNewSku(e.target.value.trim().toUpperCase()); setConfirmed(false); }}
            placeholder="KP-VT-BS-NE-L1-C1-APR2026"
          />
        </div>
      </div>

      <div style={s.issueBox}>
        {issues.length > 0
          ? issues.map((issue, i) => <div key={i} style={s.issueRow}>⛔ {issue}</div>)
          : ready
            ? <div style={s.allGoodRow}>✅ All checks passed — ready to rename</div>
            : <div style={{ color: '#888' }}>Checking…</div>
        }
      </div>

      {ready && (
        <label style={s.confirmLabel}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            style={{ marginRight: '0.5em' }}
          />
          I understand this permanently changes the database. I have double-checked both SKUs.
        </label>
      )}

      <button
        onClick={handleRename}
        disabled={!ready || !confirmed || renaming}
        style={ready && confirmed ? s.renameButtonReady : s.renameButtonDisabled}
      >
        {renaming ? 'Renaming…' : 'Rename SKU'}
      </button>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  reportGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5em',
    marginBottom: '0.25em',
  },
  generating: { fontSize: '0.83em', color: '#888', fontStyle: 'italic' },
  csvLink: { fontSize: '0.83em', fontWeight: 500 },

  // Danger section
  dangerSection: {
    border: '2px solid #e8a800',
    borderRadius: '6px',
    padding: '1.2em 1.4em',
    marginTop: '1.5em',
    background: '#fffbf0',
    position: 'relative',
    minHeight: '80px',
  },
  dangerHeader: { fontSize: '1.05em', fontWeight: 700, color: '#8a5c00', marginBottom: '0.5em' },
  dangerWarning: { color: '#6b4400', fontSize: '0.9em', marginBottom: '0.5em' },

  skuInputRow: { display: 'flex', gap: '0.75em', alignItems: 'flex-end', flexWrap: 'wrap', margin: '1em 0 0.75em' },
  skuInputGroup: { display: 'flex', flexDirection: 'column', gap: '0.25em', flex: 1, minWidth: '200px' },
  skuLabel: { fontSize: '0.8em', fontWeight: 600, color: '#555' },
  skuInput: { fontFamily: 'monospace', fontSize: '0.93em', padding: '0.4em 0.6em', border: '1px solid #ccc', borderRadius: '4px' },
  arrow: { color: '#888', fontSize: '1.3em', paddingBottom: '0.35em' },

  issueBox: { background: '#f7f7f7', border: '1px solid #ddd', borderRadius: '4px', padding: '0.6em 0.8em', margin: '0 0 0.75em', fontSize: '0.86em', minHeight: '2.2em' },
  issueRow: { color: '#b00000', marginBottom: '0.2em' },
  allGoodRow: { color: '#005e00', fontWeight: 600 },

  confirmLabel: { display: 'flex', alignItems: 'flex-start', fontSize: '0.86em', color: '#444', marginBottom: '0.75em', cursor: 'pointer' },

  renameButtonReady: { background: '#1a7f37', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5em 1.4em', fontSize: '0.95em', fontWeight: 600, cursor: 'pointer' },
  renameButtonDisabled: { background: '#d1d5db', color: '#9ca3af', border: 'none', borderRadius: '4px', padding: '0.5em 1.4em', fontSize: '0.95em', cursor: 'not-allowed' },

  // Success overlay
  successOverlay: { transition: 'opacity 0.4s ease', textAlign: 'center', padding: '0.5em 0' },
  successIcon: { fontSize: '2.5em', marginBottom: '0.2em' },
  successTitle: { fontSize: '1.2em', fontWeight: 700, color: '#005e00', marginBottom: '0.8em' },

  skuChangeDisplay: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75em', marginBottom: '1em', flexWrap: 'wrap' },
  oldSkuDisplay: { fontFamily: 'monospace', background: '#fee2e2', color: '#7f1d1d', padding: '0.25em 0.6em', borderRadius: '4px', textDecoration: 'line-through' },
  arrowDisplay: { fontSize: '1.2em', color: '#555' },
  newSkuDisplay: { fontFamily: 'monospace', background: '#dcfce7', color: '#14532d', padding: '0.25em 0.6em', borderRadius: '4px', fontWeight: 700 },

  tagWarning: { display: 'flex', alignItems: 'flex-start', gap: '0.6em', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', padding: '0.75em 1em', margin: '0 auto 1em', maxWidth: '480px', textAlign: 'left', fontSize: '0.88em', color: '#7c2d12' },
  tagWarningIcon: { fontSize: '1.2em', flexShrink: 0 },

  redirectMsg: { color: '#555', fontSize: '0.9em', marginBottom: '1em' },

  goNowBtn: { background: '#1a7f37', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.45em 1.2em', fontSize: '0.9em', fontWeight: 600, cursor: 'pointer', marginRight: '0.5em' },
  dismissBtn: { background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '0.45em 1em', fontSize: '0.9em', color: '#555', cursor: 'pointer' },
};

// ── Main page ──────────────────────────────────────────────────────────────────

const AdminTools = () => {
  const [loadVT, setLoadVT]         = useState(false);
  const [loadSR, setLoadSR]         = useState(false);
  const [loadLang, setLoadLang]     = useState(false);
  const [loadCustom, setLoadCustom] = useState(false);
  const [loadPeople, setLoadPeople] = useState(false);
  const [customTagId, setCustomTagId] = useState(-1);

  return (
    <ResponsiveContainer>
      <Segment style={{ padding: '2em 0em' }} vertical>
        <Container>
          <Header as='h1' textAlign='center'>Admin Reports</Header>

          <Header as='h3' dividing>Truency</Header>
          <ReportGrid>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
              <span style={{ fontSize: '0.83em', color: '#555' }}>Vocational Training:</span>
              {loadVT ? <TruencyReport tagId={59} /> : <ReportBtn onClick={() => setLoadVT(true)}>Generate</ReportBtn>}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
              <span style={{ fontSize: '0.83em', color: '#555' }}>Social &amp; Recreation:</span>
              {loadSR ? <TruencyReport tagId={57} /> : <ReportBtn onClick={() => setLoadSR(true)}>Generate</ReportBtn>}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
              <span style={{ fontSize: '0.83em', color: '#555' }}>Language:</span>
              {loadLang ? <TruencyReport tagId={51} /> : <ReportBtn onClick={() => setLoadLang(true)}>Generate</ReportBtn>}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
              {loadCustom
                ? <TruencyReport tagId={customTagId} />
                : <>
                    <input type="number" style={{ width: '4.5em', fontSize: '0.83em', padding: '0.25em 0.4em', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                      onChange={e => setCustomTagId(Number(e.target.value))} placeholder="Tag ID" />
                    <ReportBtn onClick={() => setLoadCustom(true)}>Custom</ReportBtn>
                  </>}
            </span>
          </ReportGrid>

          <Header as='h3' dividing>Database</Header>
          <ReportGrid>
            {loadPeople
              ? <PeopleList />
              : <ReportBtn onClick={() => setLoadPeople(true)}>People list</ReportBtn>}
            <Report reportName='numberOfCourses'     label='Courses' />
            <Report reportName='numberOfClasses'     label='Classes' />
            <Report reportName='numberOfAttendances' label='Attendances' />
          </ReportGrid>

          <Header as='h3' dividing>Attendance by block</Header>
          <AttendanceByBlock />

          <Header as='h3' dividing>Teachers</Header>
          <ReportGrid>
            <Report reportName='teacherAttendanceReport' label='Teacher attendance (current block)' />
          </ReportGrid>

          <Header as='h3' dividing>Records</Header>
          <ReportGrid>
            <Report reportName='RecordsJan2026' label='Jan 2026' />
            <Report reportName='RecordsApr2026' label='Apr 2026' />
            <Report reportName='RecordsJul2026' label='Jul 2026' />
          </ReportGrid>

          <Header as='h3' dividing>Enrolled</Header>
          <ReportGrid>
            <Report reportName='allEnrollmentsForNov2024'          label='Nov 2024' />
            <Report reportName='allEnrollmentsForJan2025'          label='Jan 2025' />
            <Report reportName='allEnrollmentsForApr2025'          label='Apr 2025' />
            <Report reportName='allEnrollmentsForJul2025'          label='Jul 2025' />
            <Report reportName='allEnrollmentsForOct2025'          label='Oct 2025' />
            <Report reportName='allEnrollmentsForJan2026'          label='Jan 2026' />
            <Report reportName='allEnrollmentsForApr2026'          label='Apr 2026 (current)' />
            <Report reportName='allEnrollmentsForJoyyaGoodsLadies' label='JG Ladies (all time)' />
          </ReportGrid>

          <Header as='h3' dividing>Participants enrolled by community</Header>
          <ReportGrid>
            <Report reportName='numberOfParticipantsEnrolledNov2024' label='Nov 2024' />
            <Report reportName='numberOfParticipantsEnrolledJan2025' label='Jan 2025' />
            <Report reportName='numberOfParticipantsEnrolledApr2025' label='Apr 2025' />
            <Report reportName='numberOfParticipantsEnrolledJul2025' label='Jul 2025' />
            <Report reportName='numberOfParticipantsEnrolledOct2025' label='Oct 2025' />
            <Report reportName='numberOfParticipantsEnrolled2025'    label='All 2025' />
          </ReportGrid>

          <SkuRenameSection />
        </Container>
      </Segment>
    </ResponsiveContainer>
  );
};

export { AdminTools };
